const TERMINAL_PUNCTUATION_PATTERN = /[.!?…]$/

// Command Center uses its own AI credential, independently of destinations.
// One capabilities query supplies every AI entry point. An answered feature
// keeps its availability through a failed refetch; without an answer, a 4xx
// refusal is settled while a network or server failure is a temporary outage.

export const AI_AVAILABILITY = Object.freeze({
  LOADING: 'loading',
  ERROR: 'error',
  READY: 'ready',
})

const AI_FEATURE_COPY = {
  text_suggestions: {
    name: 'AI suggestions',
    disabled: 'AI suggestions are not enabled.',
  },
  transcription: {
    name: 'Video analysis',
    disabled: 'Video analysis is not enabled.',
  },
  image_generation: {
    name: 'AI image generation',
    disabled: 'AI image generation is not enabled.',
  },
}

function isRefusal(error) {
  const status = Number(error?.response?.status)
  return status >= 400 && status < 500
}

export function retryAiCapabilities(failureCount, error) {
  return !isRefusal(error) && failureCount < 1
}

function serverErrorText(error) {
  const data = error?.response?.data
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim()
  if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message.trim()
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim()
  return ''
}

export function deriveAiAvailability(query) {
  if (query?.data) return { state: AI_AVAILABILITY.READY, capabilities: query.data }
  if (!query || query.isPending) return { state: AI_AVAILABILITY.LOADING }
  if (query.isError) {
    return {
      state: AI_AVAILABILITY.ERROR,
      refused: isRefusal(query.error),
      httpStatus: query.error?.response?.status ?? null,
      serverMessage: serverErrorText(query.error),
    }
  }
  return { state: AI_AVAILABILITY.READY, capabilities: {} }
}

function asSentence(text) {
  const trimmed = String(text || '').trim()
  if (!trimmed) return ''
  return TERMINAL_PUNCTUATION_PATTERN.test(trimmed) ? trimmed : `${trimmed}.`
}

// Empty when the feature is usable; otherwise one sentence beside its control.
export function aiUnavailableMessage(availability, featureKey) {
  const copy = AI_FEATURE_COPY[featureKey] || AI_FEATURE_COPY.text_suggestions
  switch (availability?.state) {
    case AI_AVAILABILITY.LOADING:
      return 'Checking AI…'
    case AI_AVAILABILITY.ERROR:
      if (!availability.refused) return 'AI is unavailable right now.'
      return asSentence(availability.serverMessage) || 'AI is not available.'
    case AI_AVAILABILITY.READY: {
      const capability = availability.capabilities?.features?.[featureKey]
      if (capability?.enabled === true) return ''
      return asSentence(capability?.reason?.message) || copy.disabled
    }
    default:
      return `${copy.name} is unavailable.`
  }
}

// Give the Generate panel the same availability hint as the other entry points.
export function imageGenerationCapabilitiesFor(availability) {
  const capabilities = availability?.state === AI_AVAILABILITY.READY ? availability.capabilities || {} : {}
  const message = aiUnavailableMessage(availability, 'image_generation')
  if (!message) return capabilities
  const imageGeneration = capabilities.features?.image_generation || {}
  return {
    ...capabilities,
    features: {
      ...(capabilities.features || {}),
      image_generation: {
        ...imageGeneration,
        enabled: false,
        reason: { ...(imageGeneration.reason || {}), message },
      },
    },
  }
}
